import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { useTournamentBySlug } from '@/data/use-tournament-by-slug';
import { useMatches } from '@/data/use-matches';
import { computePlaces, placeMap } from '@/engine';
import { SingleElimBracket } from '@/features/bracket-view/single-elim-bracket';
import { DoubleElimBracket } from '@/features/bracket-view/double-elim-bracket';
import { RoundRobinView } from '@/features/bracket-view/round-robin-view';
import { SwissView } from '@/features/bracket-view/swiss-view';
import { GroupsPlayoffView } from '@/features/bracket-view/groups-playoff-view';
import { ResultsTable } from '@/features/bracket-view/results-table';
import { useParticipants } from '@/data/use-participants';
import { AppHeader, EmptyState, ErrorState, LoadingState, StatusBadge } from '@/components/design';

const FORMAT_LABELS = {
  SINGLE_ELIM: 'Олимпийка',
  DOUBLE_ELIM: 'Double Elimination',
  ROUND_ROBIN: 'Круговая',
  SWISS: 'Швейцарка',
  GROUPS_PLAYOFF: 'Группы + плей-офф',
} as const;

export function PublicTournament() {
  const { slug } = useParams<{ slug: string }>();
  const { tournament, error } = useTournamentBySlug(slug);
  const { matches } = useMatches(tournament?.id);
  const { participants } = useParticipants(tournament?.id);

  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'bracket' | 'results'>('bracket');

  const placeByParticipant = useMemo(
    () =>
      placeMap(
        computePlaces(
          matches ?? [],
          (participants ?? []).map((p) => p.id),
          tournament?.format ?? 'SINGLE_ELIM',
          tournament?.groups
        )
      ),
    [matches, participants, tournament?.format, tournament?.groups]
  );

  const banner = (
    <div className="public-banner">
      <span className="live-dot" />
      <span>Публичная страница · обновляется в реальном времени</span>
    </div>
  );
  const headerRight = (
    <Link to="/" className="btn btn-sm">
      К приложению
    </Link>
  );

  if (error) {
    return (
      <div style={{ minHeight: '100vh' }}>
        {banner}
        <AppHeader right={headerRight} />
        <div className="container-d" style={{ paddingTop: 28, paddingBottom: 64 }}>
          <ErrorState body={`Ошибка загрузки: ${error.message}`} />
        </div>
      </div>
    );
  }

  if (tournament === undefined) {
    return (
      <div style={{ minHeight: '100vh' }}>
        {banner}
        <AppHeader right={headerRight} />
        <div className="container-d" style={{ paddingTop: 28, paddingBottom: 64 }}>
          <LoadingState />
        </div>
      </div>
    );
  }

  if (tournament === null) {
    return (
      <div style={{ minHeight: '100vh' }}>
        {banner}
        <AppHeader right={headerRight} />
        <div className="container-d" style={{ paddingTop: 28, paddingBottom: 64 }}>
          <EmptyState title="Турнир не найден" body="Проверь ссылку." />
        </div>
      </div>
    );
  }

  if (tournament.status === 'DRAFT') {
    return (
      <div style={{ minHeight: '100vh' }}>
        {banner}
        <AppHeader right={headerRight} />
        <div className="container-d" style={{ paddingTop: 28, paddingBottom: 64 }}>
          <EmptyState
            title={tournament.name}
            body="Турнир ещё в подготовке — сетка появится после старта."
          />
        </div>
      </div>
    );
  }

  const maxRound = matches && matches.length > 0 ? Math.max(...matches.map((m) => m.round)) : 0;

  const bracketView =
    !matches || matches.length === 0 ? (
      <EmptyState title="Матчи ещё не сгенерированы" />
    ) : tournament.format === 'SINGLE_ELIM' ? (
      <SingleElimBracket
        matches={matches}
        rounds={maxRound}
        placeByParticipant={placeByParticipant}
      />
    ) : tournament.format === 'DOUBLE_ELIM' ? (
      <DoubleElimBracket matches={matches} placeByParticipant={placeByParticipant} />
    ) : tournament.format === 'ROUND_ROBIN' ? (
      <RoundRobinView
        matches={matches}
        participants={participants ?? []}
        placeByParticipant={placeByParticipant}
      />
    ) : tournament.format === 'SWISS' ? (
      <SwissView
        matches={matches}
        participants={participants ?? []}
        placeByParticipant={placeByParticipant}
      />
    ) : tournament.format === 'GROUPS_PLAYOFF' ? (
      <GroupsPlayoffView
        tournament={tournament}
        matches={matches}
        participants={participants ?? []}
        placeByParticipant={placeByParticipant}
      />
    ) : (
      <EmptyState
        title="Формат пока не поддерживается"
        body={`Отображение для формата ${tournament.format} появится в следующих фазах.`}
      />
    );

  return (
    <div style={{ minHeight: '100vh' }}>
      {banner}
      <AppHeader right={headerRight} />

      <div className="container-d" style={{ paddingTop: 28, paddingBottom: 64 }}>
        <div className="t-eyebrow">turneo.app/p/{tournament.slug}</div>
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
          <h1 className="td-h1">{tournament.name}</h1>
          <StatusBadge status={tournament.status} />
        </div>
        <div className="row gap-2 t-mute" style={{ marginTop: 8 }}>
          <Calendar size={15} />
          <span>{tournament.date.toDate().toLocaleDateString('ru-RU')}</span>
          <span>·</span>
          <span>{FORMAT_LABELS[tournament.format]}</span>
          <span>·</span>
          <span>{tournament.participantCount} участников</span>
        </div>

        {participants && participants.length > 0 && (
          <div className="card-d" style={{ marginTop: 20, overflow: 'hidden' }}>
            <button
              className="row card-pad"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                gap: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setParticipantsOpen((v) => !v)}
            >
              <Users size={16} />
              <span className="td-h3">Участники</span>
              <span className="t-mono t-mute">{participants.length}</span>
              <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                {participantsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </button>
            {participantsOpen && (
              <>
                <div className="divider" />
                <div
                  className="card-pad"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 8,
                  }}
                >
                  {participants.map((p, idx) => (
                    <div key={p.id} className="row gap-2">
                      <span className="t-mono t-mute">{idx + 1}</span>
                      <span className="t-body">{p.displayName}</span>
                      <span className="t-small t-mute" style={{ marginLeft: 'auto' }}>
                        {p.players.map((pl) => pl.club).filter(Boolean).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="tabs" style={{ marginTop: 24 }}>
          <button
            className="tab"
            aria-selected={activeTab === 'bracket'}
            onClick={() => setActiveTab('bracket')}
          >
            Сетка
          </button>
          <button
            className="tab"
            aria-selected={activeTab === 'results'}
            onClick={() => setActiveTab('results')}
          >
            Итоговые места
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          {activeTab === 'bracket' ? (
            bracketView
          ) : (
            <ResultsTable
              tournament={tournament}
              matches={matches ?? []}
              participants={participants ?? []}
            />
          )}
        </div>
      </div>
    </div>
  );
}
